import React, { Component } from 'react';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  ConsentDialog,
  getConsentStatusValue,
  getPdfUrl,
} from '@unite-us/app-client-profile';
import { Button } from '@unite-us/app-components';
import { ButtonSelect } from '@unite-us/client-utils';
import { fetchCommunicationConsent } from 'actions/Consent/Contact/Group';
import { fetchGroupContact } from 'actions/Contact/Group';
import { updateGlobalState } from 'actions/Global/globalActions';
import {
  showTranslationsNewLanguages,
} from 'common/utils/FeatureFlags/flags';
import { coreApi } from 'src/api/config';
import Notifier from 'src/common/helpers/Notifier';
import { CONSENT_OPTIONS } from 'src/components/InformedConsent/constants';
import getConsentStatusDisplay from 'src/components/InformedConsent/utils/getConsentStatusDisplay';
import { env } from 'src/config/env';
import ClientProfileLink from '../ClientSummary/components/ClientProfileLink';
import './stylesheets/status-select.scss';
import findStatusConfigs from './utils/findStatusConfigs';

export class StatusSelect extends Component {
  static openPdfUrl(contact) {
    const pdfUrl = getPdfUrl(contact, env.CORE_BASE_URL);

    window.open(pdfUrl, '_blank');
  }

  constructor(props, context) {
    super(props);
    this.context = context;

    this.state = {
      initialConsentType: null,
      isConsentDialogOpen: false,
      fetchingCommunication: false,
      contact: this.props.contact,
    };

    this.generateOptions = this.generateOptions.bind(this);
    this.checkConsentPending = this.checkConsentPending.bind(this);
  }

  componentDidMount() {
    this.checkConsentPending(this.props.contact);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.contact.id !== this.props.contact.id) {
      this.checkConsentPending(this.props.contact);
    }
  }

  onCloseConsentDialog = () => {
    this.setState({ isConsentDialogOpen: false });
  }

  onOpenConsentDialog = (value) => {
    this.setState({ initialConsentType: value, isConsentDialogOpen: true });
  }

  onConsentOrCommunicationSent = ({ type }) => {
    const { groupId } = this.context;
    const { contact } = this.props;

    if (type === 'communication') {
      Notifier.dispatch('success', 'Consent Request Sent');
    }

    this.props.fetchGroupContact(groupId, contact.id).then((res) => {
      const newContact = get(res, 'data.data');

      if (type === 'consent' && get(newContact, 'consent.state') === 'accepted') {
        Notifier.dispatch('success', 'Your consent has been recorded.');
      }

      this.checkConsentPending(newContact);
      this.onCloseConsentDialog();
    });
  }

  onLanguageChange = (language) => {
    this.props.updateGlobalState({ language });
  }

  generateOptions(contact, status) {
    let options;

    if (status === 'accepted') {
      options = [
        {
          label: 'View',
          value: 'view',
          action: () => StatusSelect.openPdfUrl(contact),
        },
      ];
    } else {
      options = [
        {
          label: 'Send SMS',
          value: CONSENT_OPTIONS.PHONE_NUMBER,
          action: this.onOpenConsentDialog,
        },
        {
          label: 'Send Email',
          value: CONSENT_OPTIONS.EMAIL_ADDRESS,
          action: this.onOpenConsentDialog,
        },
        {
          label: 'Request On-Screen',
          value: CONSENT_OPTIONS.ON_SCREEN,
          action: this.onOpenConsentDialog,
        },
        {
          label: 'Upload Signed Paper',
          value: CONSENT_OPTIONS.DOCUMENT,
          action: this.onOpenConsentDialog,
        },
      ];

      if (!this.props.showNHConsent) {
        options = [
          ...options,
          {
            label: 'Upload Audio File',
            value: CONSENT_OPTIONS.AUDIO,
            action: this.onOpenConsentDialog,
          },
          {
            label: 'Provide Attestation',
            value: CONSENT_OPTIONS.ON_SCREEN_ATTESTATION,
            action: this.onOpenConsentDialog,
          },
        ];
      }
    }
    return options;
  }

  /**
   * The consent is pending if the user doesn't have a consent and
   * there is a communication created with category 'consent_request'
   */
  checkConsentPending(contact) {
    const { groupId } = this.context;
    const status = getConsentStatusValue(contact);
    this.setState({ status, contact });
    if (isEmpty(contact.consent)) {
      this.setState({ fetchingCommunication: true });
      this.props.fetchCommunicationConsent(contact.id, groupId).then((result) => {
        if (!isEmpty(get(result, 'data.data', []))) {
          this.setState({ status: 'pending' });
        }
        this.setState({ fetchingCommunication: false });
      });
    }
  }

  render() {
    const {
      isFetching,
      employee,
      language,
      shouldShowTranslationsNewLanguages,
      showNHConsent,
      showCondensedView,
      hasClientLink,
    } = this.props;
    const {
      initialConsentType, status, fetchingCommunication, contact, isConsentDialogOpen,
    } = this.state;

    if (isFetching || fetchingCommunication) { return null; }

    const placeholder = getConsentStatusDisplay(status, this.context.enums.consent.contact_status);
    const statusConfig = findStatusConfigs(status);
    const options = this.generateOptions(contact, status);

    return (
      <div className="status-select">
        {!showCondensedView && (
          (hasClientLink) ? (
            <ClientProfileLink iconSize={21}>
              <Button
                className="hover:underline cursor-text text-action-blue p-0 mr-0 font-bold"
                style={{ fontSize: '32px', fontFamily: 'Proxima Nova, sans-serif' }}
                link
                label={contact.full_name}
              />
            </ClientProfileLink>
          ) : (
            <div className="status-select__full-name display">
              {contact.full_name}
            </div>
          )
        )}
        <div className={`${!showCondensedView && hasClientLink ? 'ml-10' : ''}`}>
          <ButtonSelect
            id="status-select-options"
            className="status-select__button-select"
            options={options}
            valueKey="value"
            labelKey="label"
            placeholder={placeholder}
            icon={statusConfig.icon}
            buttonBackgroundColor={statusConfig.color}
          />
        </div>

        {isConsentDialogOpen && (
          <ConsentDialog
            contact={contact}
            employee={employee}
            initialConsentType={initialConsentType}
            language={language}
            callbacks={{
              onLanguageChange: this.onLanguageChange,
              onError: (error) => {
                if (typeof error === 'string') {
                  Notifier.dispatch('error', error);
                } else {
                  Notifier.handleErrors(error);
                }
              },
              onCommunicationSent: () => this.onConsentOrCommunicationSent({ type: 'communication' }),
              onConsentSent: () => this.onConsentOrCommunicationSent({ type: 'consent' }),
              onNavigateAway: this.onCloseConsentDialog,
              onTrackEvent: this.context.eventTracker,
            }}
            constants={{
              VERBAL_CONSENT_SCRIPT_URL: '/consent/verbal-consent-script',
              CONSENT_DOWNLOAD_URL: env.CONSENT_DOWNLOAD_URL,
              CONSENT_APP_URL: env.CONSENT_APP_URL,
            }}
            adapters={{
              coreApi,
            }}
            flags={{
              shouldShowTranslationsNewLanguages,
              showNHConsent,
            }}
          />
        )}
      </div>
    );
  }
}

StatusSelect.propTypes = {
  contact: PropTypes.object.isRequired,
  employee: PropTypes.object.isRequired,
  /* optional prop, in the instance a contact underlying data has changed but exists in store */
  isFetching: PropTypes.bool,
  language: PropTypes.string.isRequired,
  shouldShowTranslationsNewLanguages: PropTypes.bool.isRequired,
  fetchGroupContact: PropTypes.func.isRequired,
  fetchCommunicationConsent: PropTypes.func.isRequired,
  updateGlobalState: PropTypes.func.isRequired,
  showCondensedView: PropTypes.bool,
  hasClientLink: PropTypes.bool,
  showNHConsent: PropTypes.bool,
};

StatusSelect.defaultProps = {
  isFetching: false,
  showCondensedView: false,
  hasClientLink: false,
  showNHConsent: false,
};

StatusSelect.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
  enums: PropTypes.shape({
    consent: PropTypes.shape({
      contact_status: PropTypes.array.isRequired,
    }).isRequired,
  }).isRequired,
  groupId: PropTypes.string.isRequired,
};

function mapStateToProps(state) {
  const language = state.globalState.language;
  const employee = state.globalState.currentEmployee;
  const nh_consent = state.globalState.currentProvider?.group?.features?.nh_consent ||
    state.session?.currentProvider?.features?.nh_consent;

  return {
    language,
    shouldShowTranslationsNewLanguages: showTranslationsNewLanguages(state),
    employee,
    showNHConsent: nh_consent,
  };
}

export default connect(mapStateToProps, {
  updateGlobalState,
  fetchGroupContact,
  fetchCommunicationConsent,
})(StatusSelect);
